import { FormBlock } from '@qbe/ads-blocks-forms';
import { Box, Button, ButtonGroup, Dropdown, TextField, Typography } from '@qbe/ads-core';
import { INTERESTED_PARTIES_PER_COVER_LIMIT } from 'constants/domain-specific';
import {
  useAppContent,
  useAppDispatch,
  useExtraInfo,
  useFieldValidation,
  useSelectCover,
} from 'hooks';
import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { addInterestedParty, updateInterestedParty } from 'slices/extraInfo';
import { Cover } from 'types/cover';
import { InterestedPartyUI } from 'types/policy';
import { getInterestedPartiesByCover, interestedPartyUtils, sortCovers } from 'utils';

import { Portal } from '~/components/Portal';
import { useCoverOptions } from '~/hooks/useCoverOptions';
import { coverDetailsMap } from '~/rules/coverDetails';

interface UpsertInterestedPartyProps {
  onAddParty?: () => void;
  onUpdateParty?: () => void;
  onClose?: () => void;
  mode?: 'insert' | 'update';
  selectedParty?: InterestedPartyUI;
  required?: boolean;
  formId: string;
}

const UpsertInterestedParty = ({
  onAddParty,
  onUpdateParty,
  onClose,
  mode = 'insert',
  selectedParty,
  required = false,
  formId,
}: UpsertInterestedPartyProps) => {
  const { formState, control, unregister, handleSubmit, trigger } = useForm<{
    coverType?: Cover;
    partyName?: string;
  }>({
    values: {
      coverType: selectedParty?.cover,
      partyName: selectedParty?.name ?? '',
    },
  });
  if (mode === 'update' && !selectedParty) throw new Error('selectedParty missing');
  if (mode === 'insert' && !!selectedParty)
    throw new Error('selectedParty cannot be used with Insert mode');

  const { getTextContent } = useAppContent({
    basePath: 'dsme.extraInfoMoreBusinessDetailsLandlord',
  });

  const { getTextContent: getSharedContent } = useAppContent({
    basePath: 'shared',
  });

  const { completedCovers } = useSelectCover();
  const { interestedParties } = useExtraInfo();
  const { data: coverOrder } = useCoverOptions();

  const { debouncedChangeHandler, blurHandler, focusHandler, textFieldState } = useFieldValidation({
    validations: {
      required: true,
      maxLength: 60,
    },
  });

  useEffect(
    () => () => {
      unregister('partyName');
      unregister('coverType');
    },
    [unregister]
  );

  const interestedPartyCoversCount = getInterestedPartiesByCover(interestedParties!);
  const coverLessThanOrEqualToFive = (c: Cover) =>
    c in interestedPartyCoversCount
      ? interestedPartyCoversCount[c].length < INTERESTED_PARTIES_PER_COVER_LIMIT
      : true;

  const coverOptions = sortCovers(
    [
      ...(selectedParty ? [selectedParty.cover] : []),
      ...completedCovers
        .filter(coverLessThanOrEqualToFive)
        .filter((c) => coverDetailsMap[c].canHaveInterestedParties),
    ]?.filter(interestedPartyUtils.onlyUnique),
    coverOrder
  )?.map((c) => ({
    label: c,
    value: c,
  }));

  const dispatch = useAppDispatch();

  const handleUpsertInterestedParty: React.FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();
    e.stopPropagation();
    handleSubmit((form) => {
      if (!form.coverType || !form.partyName) return;
      if (mode === 'insert') {
        dispatch(
          addInterestedParty({
            cover: form.coverType,
            name: form.partyName,
          })
        );
        onAddParty?.();
      } else {
        dispatch(
          updateInterestedParty({
            id: selectedParty!.id,
            cover: form.coverType,
            name: form.partyName,
          })
        );
        onUpdateParty?.();
      }
    })(e);
  };

  return (
    <>
      <Portal>
        <form id={formId} onSubmit={handleUpsertInterestedParty} />
      </Portal>

      <Box display="flex" justifyContent="center" flexDirection="column" alignItems="center">
        {mode === 'insert' && (
          <Typography gutterBottom align="center" variant="subtitle2" component="h3">
            {getTextContent('content.detailsOfThirdPartyLabel.title')}
          </Typography>
        )}

        <FormBlock preset="dropdown">
          <Controller
            name="coverType"
            control={control}
            rules={{
              required: true,
            }}
            render={({ field: { ref, ...field }, fieldState }) => (
              <Dropdown
                {...field}
                value={field.value ?? ''}
                inputRef={ref}
                optionList={coverOptions}
                data-track="cover type"
                data-track-value="cover"
                error={fieldState.invalid}
                helperText={
                  fieldState.invalid ? getTextContent('fields.coverType.invalidText') : ''
                }
                label={getTextContent('fields.coverType.title')}
                id="coverType"
                fullWidth
                required
                onBlur={() => trigger('coverType')}
              />
            )}
          />
        </FormBlock>

        <FormBlock preset="input">
          <Controller
            control={control}
            name="partyName"
            rules={{
              required: true,
            }}
            render={({ field: { ref, ...field }, fieldState }) => {
              const isError = fieldState.invalid || textFieldState === 'error';
              return (
                <TextField
                  {...field}
                  inputRef={ref}
                  onChange={(e) => {
                    field.onChange(e);
                    debouncedChangeHandler(e.target.value);
                  }}
                  onBlur={(e) => blurHandler(e.target.value)}
                  onFocus={focusHandler}
                  id="partyName"
                  label={getTextContent('fields.partyName.title')}
                  data-track="Party name"
                  data-track-value="mask"
                  helperText={
                    isError
                      ? getTextContent('fields.partyName.invalidText')
                      : getTextContent('fields.partyName.helperText')
                  }
                  error={isError}
                  displayIcon
                  type="text"
                  required
                />
              );
            }}
          />
        </FormBlock>

        {mode === 'insert' && (
          <Box textAlign="center">
            {!required ? (
              <ButtonGroup
                size="small"
                primaryBtnProps={{
                  disabled: !formState.isValid,
                  'data-track': 'add party',
                  label: getTextContent('buttons.addParty'),
                  type: 'submit',
                  form: formId,
                }}
                secondaryBtnProps={{
                  'data-track': 'add party',
                  onClick: onClose,
                  label: getSharedContent('cancelModalButtonLabel'),
                }}
              />
            ) : (
              <Button
                data-track="add party"
                size="small"
                disabled={!formState.isValid}
                label={getTextContent('buttons.addParty')}
                type="submit"
                form={formId}
              />
            )}
          </Box>
        )}
      </Box>
    </>
  );
};

export default UpsertInterestedParty;
